import React, { useState } from 'react';
import { FaRobot, FaPaperPlane, FaSpinner, FaStar, FaMoon, FaSun, FaHeart, FaLock, FaCrown, FaLightbulb } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

const AIJathagam = () => {
    const [messages, setMessages] = useState([
        {
            id: 1,
            type: 'bot',
            text: "Namaste! 🙏 I'm your AI Jathagam assistant. I can help you with horoscope generation, compatibility analysis, and answer questions about astrology. How can I assist you today?"
        }
    ]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [questionsAsked, setQuestionsAsked] = useState(0);
    const [selectedService, setSelectedService] = useState(null);

    // Plan-based limits
    const planLimits = {
        free: { questions: 5, horoscopes: 1 },
        silver: { questions: 20, horoscopes: 5 },
        gold: { questions: 50, horoscopes: 15 },
        diamond: { questions: -1, horoscopes: -1 } // unlimited
    };

    const currentPlan = 'silver'; // This should come from user context
    const limit = planLimits[currentPlan];

    const quickActions = [
        { id: 'generate', label: 'Generate Horoscope', icon: <FaStar />, color: 'rose' },
        { id: 'compatibility', label: 'Check Compatibility', icon: <FaHeart />, color: 'rose' },
        { id: 'daily', label: 'Daily Prediction', icon: <FaSun />, color: 'amber' },
        { id: 'monthly', label: 'Monthly Forecast', icon: <FaMoon />, color: 'blue' }
    ];

    const handleQuickAction = (action) => {
        setSelectedService(action.id);
        const prompts = {
            generate: "I'd like to generate my complete horoscope. Please provide my birth details.",
            compatibility: "I want to check compatibility with my partner. What information do you need?",
            daily: "What does my horoscope say for today?",
            monthly: "Can you give me my monthly forecast?"
        };

        handleSendMessage(prompts[action.id]);
    };

    const handleSendMessage = (customMessage = null) => {
        const messageText = customMessage || inputMessage.trim();
        if (!messageText) return;

        if (limit.questions !== -1 && questionsAsked >= limit.questions) {
            setMessages(prev => [...prev, {
                id: Date.now(),
                type: 'bot',
                text: `⚠️ You've reached your ${currentPlan} plan limit of ${limit.questions} questions. Please upgrade to continue using AI Jathagam services.`,
                isLimit: true
            }]);
            return;
        }

        // Add user message
        const userMsg = {
            id: Date.now(),
            type: 'user',
            text: messageText
        };
        setMessages(prev => [...prev, userMsg]);
        setInputMessage('');
        setIsLoading(true);
        setQuestionsAsked(prev => prev + 1);

        // Simulate AI response
        setTimeout(() => {
            const responses = {
                generate: "To generate your horoscope, I'll need:\n\n📅 Date of Birth\n⏰ Time of Birth\n📍 Place of Birth\n\nPlease provide these details in the format: DD/MM/YYYY, HH:MM AM/PM, City Name",
                compatibility: "For compatibility analysis, I need:\n\n👤 Your Details:\n- Date, Time, Place of Birth\n\n💑 Partner's Details:\n- Date, Time, Place of Birth\n\nI'll analyze Guna Milan, planetary positions, and provide detailed insights.",
                daily: "🌟 Today's Prediction:\n\nCareer: Favorable day for new initiatives. Your hard work will be recognized.\n\nLove: Communication is key. Express your feelings openly.\n\nHealth: Maintain balance. Light exercise recommended.\n\nLucky Color: Blue\nLucky Number: 7",
                monthly: "🌙 Monthly Forecast:\n\nThis month brings opportunities for growth. Focus on:\n\n✨ Career advancement\n💝 Strengthening relationships\n🧘 Personal well-being\n\nBest dates: 5th, 12th, 21st\nChallenging period: 15th-18th"
            };

            const botResponse = {
                id: Date.now() + 1,
                type: 'bot',
                text: responses[selectedService] || "I understand you're interested in astrology. Could you please be more specific about what you'd like to know? You can ask about horoscope generation, compatibility, daily predictions, or any astrological questions."
            };

            setMessages(prev => [...prev, botResponse]);
            setIsLoading(false);
        }, 1500);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    return (
        <div className="max-w-[1400px] mx-auto px-4 py-8">
            {/* Header */}
            <div className="mb-8">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-rose-100 rounded-lg">
                            <FaRobot className="text-rose-600 text-2xl" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 mb-1">AI Jathagam Assistant</h1>
                            <p className="text-gray-600">Your personal astrology and horoscope assistant</p>
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-rose-600">
                                {questionsAsked}/{limit.questions === -1 ? '∞' : limit.questions}
                            </div>
                            <div className="text-sm text-gray-500">Questions Used</div>
                        </div>
                        <div className="h-8 w-px bg-gray-200"></div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-gray-900">
                                {currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)}
                            </div>
                            <div className="text-sm text-gray-500">Plan</div>
                        </div>
                    </div>
                </div>

                {/* Usage Bar */}
                <div className="mb-8 bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Usage Progress</span>
                        <span className="text-sm font-bold text-gray-900">
                            {Math.round((questionsAsked / (limit.questions === -1 ? 1 : limit.questions)) * 100)}%
                        </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                            className="bg-gradient-to-r from-rose-500 to-pink-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: limit.questions === -1 ? '100%' : `${(questionsAsked / limit.questions) * 100}%` }}
                        ></div>
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <FaLightbulb className="text-amber-500" />
                    Quick Actions
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {quickActions.map(action => (
                        <button
                            key={action.id}
                            onClick={() => handleQuickAction(action)}
                            className={`bg-white border border-gray-200 rounded-lg p-4 hover:border-${action.color}-300 hover:shadow-md transition-all text-left group`}
                        >
                            <div className={`text-${action.color}-600 text-xl mb-3`}>{action.icon}</div>
                            <p className="font-medium text-gray-900 text-sm">{action.label}</p>
                        </button>
                    ))}
                </div>
            </div>

            {/* Chat Container */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                {/* Chat Header */}
                <div className="border-b border-gray-200 p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-rose-100 rounded-lg">
                            <FaRobot className="text-rose-600" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900">Chat with AI Assistant</h3>
                            <p className="text-sm text-gray-500">Ask questions about astrology, horoscopes, and compatibility</p>
                        </div>
                    </div>
                </div>

                {/* Messages Area */}
                <div className="h-[500px] overflow-y-auto p-4 space-y-4">
                    <AnimatePresence>
                        {messages.map((message) => (
                            <motion.div
                                key={message.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                            >
                                <div className={`max-w-[80%] rounded-lg p-4 ${
                                    message.type === 'user' 
                                        ? 'bg-rose-600 text-white' 
                                        : message.isLimit 
                                            ? 'bg-red-50 border border-red-200 text-red-800' 
                                            : 'bg-gray-50 text-gray-800'
                                }`}>
                                    {message.type === 'bot' && !message.isLimit && (
                                        <div className="flex items-center gap-2 mb-2">
                                            <FaRobot className="text-rose-600" size={14} />
                                            <span className="text-xs font-bold text-rose-600">AI Assistant</span>
                                        </div>
                                    )}
                                    <p className="text-sm leading-relaxed whitespace-pre-line">{message.text}</p>
                                </div>
                            </motion.div>
                        ))}
                    </AnimatePresence>

                    {isLoading && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="flex justify-start"
                        >
                            <div className="bg-gray-50 rounded-lg p-4">
                                <div className="flex items-center gap-2">
                                    <FaSpinner className="animate-spin text-rose-600" />
                                    <span className="text-sm text-gray-600">Analyzing your query...</span>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </div>

                {/* Input Area */}
                <div className="border-t border-gray-200 p-4">
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Ask me anything about astrology, horoscopes, or compatibility..."
                            className="flex-1 px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent outline-none text-sm"
                            disabled={limit.questions !== -1 && questionsAsked >= limit.questions}
                        />
                        <button
                            onClick={() => handleSendMessage()}
                            disabled={!inputMessage.trim() || (limit.questions !== -1 && questionsAsked >= limit.questions)}
                            className="px-6 py-3 bg-rose-600 text-white rounded-lg font-medium hover:bg-rose-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            <FaPaperPlane size={14} />
                            Send
                        </button>
                    </div>
                    {limit.questions !== -1 && questionsAsked >= limit.questions && (
                        <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                            <div className="flex items-center gap-2">
                                <FaLock className="text-red-600" size={14} />
                                <p className="text-sm text-red-700 font-medium">
                                    Limit reached. Upgrade your plan to continue using AI Jathagam.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Plan Info */}
            <div className="mt-8 bg-gradient-to-r from-rose-50 to-blue-50 rounded-lg border border-rose-100 p-6">
                <div className="flex items-start gap-4">
                    <div className="p-3 bg-white rounded-lg shadow-sm">
                        <FaCrown className="text-amber-500 text-xl" />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-1">Upgrade for Unlimited Access</h3>
                        <p className="text-gray-600 text-sm mb-3">
                            Diamond plan members get unlimited questions, detailed horoscope reports, 
                            and priority AI assistance.
                        </p>
                        <button className="px-5 py-2 bg-rose-600 text-white text-sm font-medium rounded-lg hover:bg-rose-700">
                            Upgrade Plan
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AIJathagam;